import {
  Box,
  Stack,
  Typography,
  useTheme,
  Link,
  FormControl,
  DialogContentText,
  Button,
  Alert,
  BuildingIcon
} from "@carrotfertility/carotene-core"
import { Form, FormAutocomplete, FormButton, FormSelect, InfoDialog } from "@carrotfertility/carotene-core-x"
import React from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Helmet } from "react-helmet"
import { RequestedProviderType } from "#/pages/provider-finder/types/ProviderRequest"
import { GenderIdentityFormSelect } from "./GenderIdentityFormSelect"
import { Redirect } from "react-router-dom"
import { Loading } from "../Loading"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageHeader } from "#/features/global-ui/page-header"
import { TelemedicineSection } from "./TelemedicineSection"
import { HealthInsuranceAutocompleteOption, useProviderRecommendationForm } from "./useProviderRecommendationForm"
import { FormTextFieldWithCharacterCount } from "#/components/carrot-plans/shared"
import { renderSelectWithPlaceholder } from "#/services/common-forms/renderSelectValueUtil"
import { useHealthInsuranceOptions } from "../../hooks/useHealthInsuranceOptions"
import { LocationPicker } from "#/pages/provider-finder/components/location-picker/LocationPicker"
import { Controller, useFormContext } from "react-hook-form"

function FormLocationPicker({
  name,
  label,
  setLocationPickerStringValue,
  defaultLocationValue
}: {
  name: string
  label: string
  setLocationPickerStringValue: (input: string) => void
  defaultLocationValue: google.maps.places.AutocompletePrediction
}): JSX.Element {
  const {
    formState: { errors, isSubmitting },
    control
  } = useFormContext()

  const hasError = Boolean(errors[name])

  return (
    <FormControl error={hasError}>
      <Controller
        {...{ control, name }}
        rules={{ required: "Required" }}
        render={({ field }) => (
          <LocationPicker
            label={label}
            setLocationPickerStringValue={setLocationPickerStringValue}
            hasError={hasError}
            helperText={hasError ? (errors[name]?.message as string) : null}
            isDisabled={isSubmitting}
            isRequired
            onChange={(_, option) => field.onChange(option)}
            defaultLocationValue={defaultLocationValue}
          />
        )}
      />
    </FormControl>
  )
}

function AlertBox(): JSX.Element {
  return (
    <Alert severity="error">
      <FormattedMessage
        defaultMessage="Something went wrong on our end. Please try again. If the problem persists, <link>contact us.</link>"
        values={{
          link: (linkText) => (
            <Link color="inherit" target="_blank" href={"/talk-to-carrot/care-navigation"}>
              {linkText}
            </Link>
          )
        }}
      />
    </Alert>
  )
}

function HealthInsuranceAutocomplete(): JSX.Element {
  const intl = useIntl()
  const healthInsuranceOptionsResult = useHealthInsuranceOptions()

  if (healthInsuranceOptionsResult.isLoading) {
    return null
  }

  return (
    <FormAutocomplete
      name="healthInsurance"
      label={intl.formatMessage({ defaultMessage: "Health insurance" })}
      controllerProps={{ rules: { required: "Required" } }}
      placeholder={intl.formatMessage({ defaultMessage: "Select option" })}
      options={healthInsuranceOptionsResult.data
        .map((name: string) => ({
          label: name,
          value: name
        }))
        .sort((a: HealthInsuranceAutocompleteOption, b: HealthInsuranceAutocompleteOption) =>
          a.label.localeCompare(b.label)
        )}
      helperText={intl.formatMessage(
        {
          defaultMessage: "If you don't see your insurance provider listed, please <link>contact us</link>"
        },
        {
          link: (linkContent) => (
            <Link
              id="provider-recommendations-insurance-not-listed-contact-link"
              variant="inherit"
              color="inherit"
              target="_blank"
              href="/talk-to-carrot/care-navigation"
            >
              {linkContent}
            </Link>
          )
        }
      )}
    />
  )
}

function SubmissionDialog({ isSuccess, onClose }: { isSuccess: boolean; onClose: () => void }): JSX.Element {
  return (
    <InfoDialog
      title={useIntl().formatMessage({ defaultMessage: "Request submitted" })}
      open={isSuccess}
      primaryAction={
        <Button onClick={onClose}>
          <FormattedMessage defaultMessage="Close" />
        </Button>
      }
    >
      <DialogContentText>
        <FormattedMessage defaultMessage="We'll send you a message with your recommendations within one business day." />
      </DialogContentText>
    </InfoDialog>
  )
}

export function ProviderRecommendationRequestForm({
  providerRequested
}: {
  providerRequested: RequestedProviderType
}): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const {
    isLoading,
    shouldRedirect,
    redirectPath,
    isError,
    isSuccess,
    variantContent,
    languageOptions,
    showTelemedicineSection,
    defaultLocation,
    defaultLocationString,
    defaultHealthInsurance,
    setLocationPickerStringValue,
    onSubmitHandler,
    onClose
  } = useProviderRecommendationForm(providerRequested)

  if (isLoading) {
    return <Loading />
  }

  if (shouldRedirect) {
    return <Redirect to={redirectPath} />
  }

  return (
    <PageLayout
      header={
        <Stack marginBottom={theme.spacing(theme.tokens.spacing.xl)} spacing={theme.spacing(theme.tokens.spacing.lg)}>
          <PageHeader pageTitle={<FormattedMessage defaultMessage="Provider finder" />} />
        </Stack>
      }
      variant="form"
    >
      <Helmet>
        <title>{variantContent.pageTitle}</title>
      </Helmet>
      <Stack direction="column" spacing={theme.spacing(theme.tokens.spacing.lg)}>
        <Typography variant="h2">{variantContent.pageHeading}</Typography>
        {showTelemedicineSection && (
          <>
            <TelemedicineSection />
            <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.sm)} alignItems="center">
              <BuildingIcon />
              <Typography variant="h3">
                <FormattedMessage defaultMessage="In person" />
              </Typography>
            </Stack>
          </>
        )}
        <Typography>{variantContent.pageDescription}</Typography>
        {isError && <AlertBox />}
        <Form
          onSubmit={onSubmitHandler}
          defaultValues={{ location: defaultLocationString, healthInsurance: defaultHealthInsurance }}
        >
          <Stack direction="column" spacing={theme.spacing(theme.tokens.spacing.lg)}>
            <HealthInsuranceAutocomplete />
            <FormLocationPicker
              name="location"
              label={intl.formatMessage({ defaultMessage: "Preferred location" })}
              setLocationPickerStringValue={setLocationPickerStringValue}
              defaultLocationValue={defaultLocation}
            />
            <FormSelect
              name="language"
              label={intl.formatMessage({ defaultMessage: "Language" })}
              renderValue={renderSelectWithPlaceholder(
                languageOptions,
                intl.formatMessage({ defaultMessage: "Select option" })
              )}
              options={languageOptions}
            />
            <GenderIdentityFormSelect />
            <FormTextFieldWithCharacterCount
              maxLength={1600}
              rows={4}
              label={variantContent.additionalDetailsLabel}
              placeholder={variantContent.additionalDetailsPlaceholder}
              name="additionalDetails"
            />
            <Box
              display="flex"
              flexDirection="row-reverse"
              marginBottom={(theme) => theme.spacing(theme.tokens.spacing.lg)}
            >
              <FormButton type="submit">
                <FormattedMessage defaultMessage="Submit request" />
              </FormButton>
            </Box>
          </Stack>
        </Form>
        <SubmissionDialog isSuccess={isSuccess} onClose={onClose} />
      </Stack>
    </PageLayout>
  )
}
